import React, {
  createContext,
  useContext,
  ReactNode,
} from "react";
import { Contact } from "@visitproof/types";
import { ContactService } from "../services/ContactService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";

interface ContactContextProps {
  createContact: (
    contactData: Omit<
      Contact,
      "id" | "createdAt" | "createdBy" | "updatedAt" | "accountId"
    > & { parentId: string },
  ) => Promise<string>;
  updateContact: (
    contactId: string,
    updatedData: Partial<Omit<Contact, "id" | "createdAt" | "updatedAt">>,
  ) => void;
  deleteContactById: (contactId: string) => void;
}

const ContactContext = createContext<ContactContextProps | undefined>(
  undefined,
);

export const useContact = (): ContactContextProps => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error("useContact must be used within a ContactProvider");
  }
  return context;
};

export const ContactProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();

  const createContact = async (
    contactData: Omit<
      Contact,
      "id" | "createdAt" | "createdBy" | "updatedAt" | "accountId"
    > & { parentId: string },
  ): Promise<string> => {
    if (!account?.id || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const newContactId = await ContactService.createContact({
      ...contactData,
      accountId: account.id,
      createdBy: user.uid,
    });
    return newContactId;
  };

  const updateContact = async (
    contactId: string,
    updatedData: Partial<Omit<Contact, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await ContactService.updateContact(contactId, updatedData);
  };

  const deleteContactById = async (contactId: string) => {
    await ContactService.deleteContact(contactId);
  };

  return (
    <ContactContext.Provider
      value={{
        createContact,
        updateContact,
        deleteContactById,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
};
